/*
 * Banyomda storefront filtering
 *
 * Architecture note:
 * - Filtering uses native <form> and <select> elements.
 * - No customized built-in elements (form[is=...], select[is=...], a[is=...]).
 * - A delegated controller owns filter state and Section Rendering requests.
 * - This is intentionally independent of the DOM position of the mobile drawer;
 *   DrawerComponent may temporarily move the drawer to <body>.
 */

const facetResponseCache = new Map();
const FACET_CACHE_TTL = 60 * 1000;
const FACET_CACHE_MAX_ENTRIES = 12;
const FACET_REQUEST_TIMEOUT = 15000;
const facetControllers = new WeakMap();
let facetRequestSequence = 0;

function getFacetForm(source = null) {
  if (source instanceof HTMLFormElement && source.classList.contains('facet-form')) return source;

  const candidate = source?.closest?.('form.facet-form');
  if (candidate) return candidate;

  const isMobileViewport = window.matchMedia?.('(max-width: 1279px)').matches;
  if (isMobileViewport) {
    const drawerForm = document.querySelector('#DrawerFacetFiltersContainer form.facet-form');
    if (drawerForm) return drawerForm;
  }

  return document.querySelector('#FacetFiltersContainer form.facet-form, form.facet-form');
}

function getFacetSectionId(form) {
  if (!form) return null;
  return FoxTheme.utils.getSectionId(form);
}

function getFacetController(form) {
  if (!form) return null;
  let controller = facetControllers.get(form);
  if (!controller) {
    controller = new FacetController(form);
    facetControllers.set(form, controller);
  }
  return controller;
}

function getFacetResponseCache(url) {
  const entry = facetResponseCache.get(url);
  if (!entry) return null;

  if (Date.now() - entry.timestamp > FACET_CACHE_TTL) {
    facetResponseCache.delete(url);
    return null;
  }

  facetResponseCache.delete(url);
  facetResponseCache.set(url, entry);
  return entry.text;
}

function setFacetResponseCache(url, text) {
  facetResponseCache.delete(url);
  facetResponseCache.set(url, { text, timestamp: Date.now() });

  while (facetResponseCache.size > FACET_CACHE_MAX_ENTRIES) {
    const oldestKey = facetResponseCache.keys().next().value;
    facetResponseCache.delete(oldestKey);
  }
}

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');

    if (!this.minPriceInput || !this.maxPriceInput || !this.minRangeInput || !this.maxRangeInput) return;

    this.minPriceInput.addEventListener('focus', this.selectInput);
    this.maxPriceInput.addEventListener('focus', this.selectInput);
    this.minPriceInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxPriceInput.addEventListener('change', this.handleInputMaxChange.bind(this));

    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  selectInput(event) {
    event.target.select();
  }

  handleInputMinChange(event) {
    event.preventDefault();
    const max = parseInt(this.maxPriceInput.value || event.target.max, 10);
    const min = parseInt(event.target.min || 0, 10);
    const value = parseInt(event.target.value || min, 10);

    event.target.value = Math.max(Math.min(value, max - 1), min);
    this.minRangeInput.value = event.target.value;
    this.updateRangeVisuals();
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    const min = parseInt(this.minPriceInput.value || event.target.min || 0, 10);
    const max = parseInt(event.target.max, 10);
    const value = parseInt(event.target.value || max, 10);

    event.target.value = Math.min(Math.max(value, min + 1), max);
    this.maxRangeInput.value = event.target.value;
    this.updateRangeVisuals();
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.minPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.maxPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMinInput(event) {
    const max = parseInt(this.maxPriceInput.value || event.target.max, 10);
    event.target.value = Math.min(parseInt(event.target.value, 10), max - 1);
    this.minPriceInput.value = event.target.value;
    this.updateRangeVisuals();
  }

  handleRangeMaxInput(event) {
    const min = parseInt(this.minPriceInput.value || event.target.min || 0, 10);
    event.target.value = Math.max(parseInt(event.target.value, 10), min + 1);
    this.maxPriceInput.value = event.target.value;
    this.updateRangeVisuals();
  }

  updateRangeVisuals() {
    const rangeMax = parseInt(this.maxRangeInput.max, 10) || 1;
    const min = parseInt(this.minRangeInput.value, 10) || 0;
    const max = parseInt(this.maxRangeInput.value, 10) || rangeMax;
    const wrapper = this.minRangeInput.parentElement;

    wrapper?.style.setProperty('--range-min', `${(min / rangeMax) * 100}%`);
    wrapper?.style.setProperty('--range-max', `${(max / rangeMax) * 100}%`);
  }
}
if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);

class FacetController {
  constructor(form) {
    this.form = form;
    this.dirty = false;
    this.abortController = null;
    this.previewAbortController = null;
    this.previewTimer = null;
    this.previewToken = 0;
    this.requestToken = 0;
  }

  get isDrawerForm() {
    return !!this.form.closest('#DrawerFacetFiltersContainer');
  }

  markDirty() {
    this.dirty = true;
    this.updateDraftCount();
  }

  setApplyPreview(text = '', { pending = false } = {}) {
    const button = document.querySelector('#FacetDrawer [data-facet-apply]');
    if (!button) return;
    const label = button.querySelector('[data-facet-apply-label]');
    const count = button.querySelector('[data-facet-result-preview]');
    if (label) label.textContent = pending ? 'Sonuçlar hesaplanıyor…' : (text || 'Filtreleri Uygula');
    if (count) {
      count.textContent = '';
      count.hidden = true;
    }
    button.classList.toggle('is-previewing', pending);
  }

  cancelPreview() {
    if (this.previewTimer) window.clearTimeout(this.previewTimer);
    this.previewTimer = null;
    this.previewAbortController?.abort();
    this.previewAbortController = null;
    this.previewToken += 1;
    const button = document.querySelector('#FacetDrawer [data-facet-apply]');
    button?.classList.remove('is-previewing');
  }

  scheduleDraftResultPreview() {
    if (!this.isDrawerForm) return;
    this.cancelPreview();
    if (!this.hasEffectiveDraftChanges()) {
      this.setApplyPreview('Filtreleri Uygula');
      return;
    }

    const token = ++this.previewToken;
    this.setApplyPreview('', { pending: true });
    this.previewTimer = window.setTimeout(() => this.previewDraftResults(token), 280);
  }

  async previewDraftResults(token) {
    if (token !== this.previewToken || !this.form?.isConnected) return;
    const url = this.buildUrl().toString();
    const cached = getFacetResponseCache(url);
    const currentButton = document.querySelector('#FacetDrawer [data-facet-apply]');

    let timeoutId = null;
    try {
      let responseText = cached;
      if (!responseText) {
        this.previewAbortController = new AbortController();
        timeoutId = window.setTimeout(() => this.previewAbortController?.abort(), 8000);
        const response = await fetch(url, {
          signal: this.previewAbortController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Facet preview failed: ${response.status}`);
        responseText = await response.text();
        setFacetResponseCache(url, responseText);
      }

      if (token !== this.previewToken) return;
      const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
      const sourceCount = parsedHTML.getElementById('ProductCount');
      const rawText = sourceCount?.textContent?.replace(/\s+/g, ' ').trim() || '';
      const match = rawText.match(/\d[\d\s.]*/);
      const resultText = match ? match[0].replace(/[\s.]/g, '') : '';

      if (!resultText) {
        this.setApplyPreview('Sonuçları Göster');
        return;
      }

      const count = Number(resultText);
      const safeLabel = Number.isFinite(count) ? `${count.toLocaleString('tr-TR')} Ürünü Göster` : 'Sonuçları Göster';
      this.setApplyPreview(safeLabel);
      if (currentButton) currentButton.dataset.previewCount = Number.isFinite(count) ? String(count) : '';
    } catch (error) {
      if (error?.name === 'AbortError' || token !== this.previewToken) return;
      console.warn('[Banyomda] Mobile filter result preview unavailable.', error);
      this.setApplyPreview('Sonuçları Göster');
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
      this.previewAbortController = null;
    }
  }

  hasEffectiveDraftChanges() {
    if (!this.form?.isConnected) return false;
    const draftUrl = new URL(this.publicUrl(this.buildUrl()), window.location.origin);
    const currentUrl = new URL(window.location.href);
    draftUrl.hash = '';
    currentUrl.hash = '';
    return draftUrl.pathname + draftUrl.search !== currentUrl.pathname + currentUrl.search;
  }

  buildUrl() {
    const url = new URL(this.form.action || window.location.pathname, window.location.origin);
    url.search = '';
    const currentParams = new URLSearchParams(window.location.search);
    const formParams = new URLSearchParams(new FormData(this.form));
    const formKeys = new Set(formParams.keys());

    // Preserve non-filter query state such as search options and the current
    // sort order, but never duplicate a parameter that the form explicitly
    // owns (notably `q` on search pages). Filter parameters are rebuilt from
    // the live form so stale values cannot leak into the next request.
    currentParams.forEach((value, key) => {
      if (key === 'section_id' || key === 'page' || key.startsWith('filter.') || formKeys.has(key)) return;
      url.searchParams.append(key, value);
    });

    formParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
      if (url.searchParams.get(key) === '') url.searchParams.delete(key);
    });

    url.searchParams.set('section_id', getFacetSectionId(this.form));
    return url;
  }

  publicUrl(url) {
    const publicUrl = new URL(url, window.location.origin);
    publicUrl.searchParams.delete('section_id');
    return publicUrl.toString();
  }

  updateHistory(url, mode = 'push') {
    if (!this.form.hasAttribute('data-history') || mode === 'none') return;

    const nextUrl = this.publicUrl(url);
    if (nextUrl === window.location.href) return;

    if (mode === 'replace') history.replaceState({ facetState: true }, '', nextUrl);
    else history.pushState({ facetState: true }, '', nextUrl);
  }

  updateDraftCount() {
    const form = this.form;
    if (!form.isConnected) return;

    const checked = form.querySelectorAll('input[type="checkbox"][name]:checked').length;
    const minPrice = form.querySelector('input[name="filter.v.price.gte"]')?.value || '';
    const maxPrice = form.querySelector('input[name="filter.v.price.lte"]')?.value || '';
    const count = checked + (minPrice !== '' || maxPrice !== '' ? 1 : 0);

    document.querySelectorAll('#FacetDrawer facet-count, .facet-drawer-button-wrap facet-count').forEach((element) => {
      element.textContent = count ? `(${count})` : '';
      element.hidden = count === 0;
    });

    document.querySelectorAll('[data-facet-drawer-count]').forEach((element) => {
      element.textContent = String(count);
      element.hidden = count === 0;
    });

    document.querySelectorAll('[data-facet-clear-draft]').forEach((element) => {
      element.hidden = count === 0;
    });

    const applyButton = document.querySelector('#FacetDrawer [data-facet-apply]');
    if (applyButton) {
      const canApply = this.hasEffectiveDraftChanges();
      applyButton.disabled = !canApply;
      applyButton.setAttribute('aria-disabled', String(!canApply));
    }
  }

  clearDraft() {
    const form = this.form;
    form.querySelectorAll('input[type="checkbox"][name]').forEach((input) => {
      input.checked = false;
    });

    const minPrice = form.querySelector('input[name="filter.v.price.gte"]');
    const maxPrice = form.querySelector('input[name="filter.v.price.lte"]');
    if (minPrice) minPrice.value = '';
    if (maxPrice) maxPrice.value = '';

    form.querySelectorAll('price-range').forEach((range) => {
      const min = range.querySelector('input[type="range"]:first-child');
      const max = range.querySelector('input[type="range"]:last-child');
      if (!min || !max) return;
      min.value = min.min;
      max.value = max.max;
      range.querySelector('.facet-range-wrapper')?.style.setProperty('--range-min', '0%');
      range.querySelector('.facet-range-wrapper')?.style.setProperty('--range-max', '100%');
    });

    this.markDirty();
    this.scheduleDraftResultPreview();
  }

  resetDraft() {
    if (!this.form.isConnected) return;
    this.form.reset();
    this.form.querySelectorAll('price-range').forEach((range) => {
      const minPrice = range.querySelector('input[name="filter.v.price.gte"]');
      const maxPrice = range.querySelector('input[name="filter.v.price.lte"]');
      const minRange = range.querySelector('input[type="range"]:first-child');
      const maxRange = range.querySelector('input[type="range"]:last-child');
      if (!minRange || !maxRange) return;
      minRange.value = minPrice?.value || minRange.min;
      maxRange.value = maxPrice?.value || maxRange.max;
      range.querySelector('.facet-range-wrapper')?.style.setProperty('--range-min', `${(parseFloat(minRange.value) / (parseFloat(minRange.max) || 1)) * 100}%`);
      range.querySelector('.facet-range-wrapper')?.style.setProperty('--range-max', `${(parseFloat(maxRange.value) / (parseFloat(maxRange.max) || 1)) * 100}%`);
    });
    this.dirty = false;
    this.cancelPreview();
    this.setApplyPreview('Filtreleri Uygula');
    this.updateDraftCount();
  }

  setErrorState(message = '') {
    document.querySelectorAll('[data-facet-error]').forEach((element) => {
      element.textContent = message;
      element.hidden = !message;
    });
  }

  beforeRenderSection(requestToken) {
    this.setErrorState('');
    const container = document.getElementById('ProductGridContainer');
    const loadings = document.querySelectorAll('[data-facet-loading], [data-facet-apply]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    container?.setAttribute('aria-busy', 'true');
    this.form.setAttribute('aria-busy', 'true');

    if (container) {
      FoxTheme.Motion.timeline([[container, { y: translateY, opacity: 0 }, { duration: 0 }]]);
    }

    if (!this.isDrawerForm) {
      setTimeout(() => {
        if (requestToken !== this.requestToken) return;
        const target = document.querySelector('.collection');
        if (!target) return;
        window.scrollTo({
          top: target.getBoundingClientRect().top + window.scrollY - 120,
          behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth',
        });
      }, 100);
    }

    loadings.forEach((loading) => loading.classList.add('btn--loading'));
  }

  afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container ? container.querySelectorAll('.product-card') : [];
    const loadings = document.querySelectorAll('[data-facet-loading], [data-facet-apply]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    if (container) {
      FoxTheme.Motion.timeline([
        [container, { y: [translateY, 0], opacity: [0, 1] }],
        [items, { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
          { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) }],
      ]);
      container.setAttribute('aria-busy', 'false');
    }

    loadings.forEach((loading) => loading.classList.remove('btn--loading'));
    document.querySelectorAll('.facet-form[aria-busy="true"]').forEach((form) => form.removeAttribute('aria-busy'));
    document.dispatchEvent(new CustomEvent('collection:rerendered'));
  }

  handleRenderError(message = 'Filtreler yüklenemedi. Lütfen tekrar deneyin.') {
    document.querySelectorAll('[data-facet-loading], [data-facet-apply]').forEach((loading) => loading.classList.remove('btn--loading'));
    document.getElementById('ProductGridContainer')?.setAttribute('aria-busy', 'false');
    document.querySelectorAll('.facet-form[aria-busy="true"]').forEach((form) => form.removeAttribute('aria-busy'));
    this.setErrorState(message);
    document.dispatchEvent(new CustomEvent('collection:render-error', { detail: { message } }));
  }

  renderSection(url, event = null, { history = 'push', closeDrawer = false } = {}) {
    const requestToken = ++facetRequestSequence;
    this.requestToken = requestToken;
    this.abortController?.abort();

    const cached = getFacetResponseCache(url);
    if (cached) {
      this.renderResponse(cached, url, event, { history, closeDrawer, requestToken });
      return;
    }

    this.beforeRenderSection(requestToken);
    this.abortController = new AbortController();
    const requestStartedAt = performance.now();
    const timeoutId = window.setTimeout(() => this.abortController?.abort(), FACET_REQUEST_TIMEOUT);

    fetch(url, { signal: this.abortController.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Facet request failed: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (requestToken !== this.requestToken) return;
        setFacetResponseCache(url, responseText);
        const execution = performance.now() - requestStartedAt;
        const delay = execution < 250 ? 250 - execution : 0;
        setTimeout(() => {
          if (requestToken !== this.requestToken) return;
          this.renderResponse(responseText, url, event, { history, closeDrawer, requestToken });
        }, delay);
      })
      .catch((error) => {
        if (requestToken !== this.requestToken) return;
        if (error.name === 'AbortError') {
          // A user-generated second request cancels the first one. A timeout is
          // different: it is a real failure and must be surfaced to the user.
          if (performance.now() - requestStartedAt >= FACET_REQUEST_TIMEOUT - 50) {
            console.error('[Banyomda] Collection filter request timed out.', error);
            this.handleRenderError('Filtreler yüklenemedi. Lütfen tekrar deneyin.');
          }
          return;
        }
        console.error('[Banyomda] Collection filter update failed.', error);
        this.handleRenderError('Filtreler yüklenemedi. Lütfen tekrar deneyin.');
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
      });
  }

  renderResponse(responseText, url, event, { history = 'push', closeDrawer = false, requestToken } = {}) {
    if (requestToken !== this.requestToken) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const sourceGrid = parsedHTML.getElementById('ProductGridContainer');
    const sourceFacets = parsedHTML.getElementById('FacetFiltersContainer');
    const sourceDrawer = parsedHTML.getElementById('DrawerFacetFiltersContainer');
    const sourceActive = parsedHTML.getElementById('FacetFiltersActive');
    const sourceCount = parsedHTML.getElementById('ProductCount');
    const sourceSort = parsedHTML.getElementById('SortByContainer');
    const sourceMobileSort = parsedHTML.getElementById('SortByContainerMobile');

    this.replaceInner('ProductGridContainer', sourceGrid);
    this.replaceInner('FacetFiltersActive', sourceActive);
    this.replaceInner('FacetFiltersContainer', sourceFacets);
    this.replaceInner('ProductCount', sourceCount);
    this.replaceInner('SortByContainer', sourceSort);
    this.replaceInner('SortByContainerMobile', sourceMobileSort);

    const drawerTarget = document.getElementById('DrawerFacetFiltersContainer');
    if (drawerTarget && sourceDrawer) {
      drawerTarget.innerHTML = sourceDrawer.innerHTML;
    }

    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, {
      responseText,
      parsedHTML,
    });

    this.dirty = false;
    this.cancelPreview();
    this.afterRenderSection();
    this.updateHistory(url, history);

    if (closeDrawer) {
      document.getElementById('FacetDrawer')?.hide?.();
    }

    // The live form may have been replaced. The committed URL is now the source
    // of truth, so no draft state is carried across the render.
    this.updateDraftCountOnLiveDrawer();
  }

  replaceInner(id, source) {
    const target = document.getElementById(id);
    if (!target || !source) return;
    target.innerHTML = source.innerHTML;
  }

  updateDraftCountOnLiveDrawer() {
    const drawerForm = document.querySelector('#DrawerFacetFiltersContainer form.facet-form');
    if (!drawerForm) return;
    const controller = getFacetController(drawerForm);
    controller.dirty = false;
    controller.updateDraftCount();
  }
}

function handleFacetFormChange(event) {
  const form = event.target?.closest?.('form.facet-form');
  if (!form) return;
  if (!event.target.name) return;

  const controller = getFacetController(form);
  controller.markDirty();
  if (controller.isDrawerForm) controller.scheduleDraftResultPreview();

  // Desktop: filtering is immediate.
  // Mobile drawer: selections remain a draft until Apply is pressed.
  if (!controller.isDrawerForm) {
    controller.renderSection(controller.buildUrl().toString(), event, { history: 'push', closeDrawer: false });
  }
}

function handleFacetFormSubmit(event) {
  const form = event.target?.closest?.('form.facet-form');
  if (!form) return;

  event.preventDefault();
  const controller = getFacetController(form);
  if (!controller.hasEffectiveDraftChanges()) return;
  controller.renderSection(controller.buildUrl().toString(), event, {
    history: 'push',
    closeDrawer: controller.isDrawerForm,
  });
}

const FACET_DELEGATION_KEY = '__BanyomdaFacetDelegationV522';
if (!window[FACET_DELEGATION_KEY]) {
  window[FACET_DELEGATION_KEY] = true;

  document.addEventListener('change', handleFacetFormChange);
  document.addEventListener('submit', handleFacetFormSubmit, true);

document.addEventListener('change', (event) => {
  const select = event.target?.closest?.('#SortByContainer select, #SortByContainerMobile select');
  if (!select) return;

  const form = getFacetForm();
  if (!form) return;

  const url = new URL(window.location.href);
  url.searchParams.set('sort_by', select.value);
  url.searchParams.set('section_id', getFacetSectionId(form));
  url.searchParams.delete('page');

  getFacetController(form).renderSection(url.toString(), event, { history: 'push', closeDrawer: false });
});

document.addEventListener('click', (event) => {
  const clearButton = event.target.closest('[data-facet-clear-draft]');
  if (clearButton) {
    const drawerForm = document.querySelector('#FacetDrawer form.facet-form');
    if (!drawerForm) return;
    event.preventDefault();
    getFacetController(drawerForm).clearDraft();
    return;
  }

  const applyButton = event.target.closest('[data-facet-apply]');
  if (applyButton) {
    const drawer = applyButton.closest('#FacetDrawer');
    const form = drawer?.querySelector('form.facet-form');
    if (!form) return;

    event.preventDefault();
    const controller = getFacetController(form);
    if (!controller.hasEffectiveDraftChanges()) {
      controller.resetDraft();
      drawer.hide?.();
      return;
    }

    controller.renderSection(controller.buildUrl().toString(), event, {
      history: 'push',
      closeDrawer: true,
    });
    return;
  }

  const removeLink = event.target.closest('a.facet-remove, a.facet-clear-all, a[data-facet-remove]');
  if (removeLink) {
    const form = getFacetForm();
    if (!form) return;

    event.preventDefault();
    const url = new URL(removeLink.href, window.location.origin);
    url.searchParams.set('section_id', getFacetSectionId(form));
    getFacetController(form).renderSection(url.toString(), event, { history: 'push', closeDrawer: false });
  }
});

window.addEventListener('popstate', () => {
  const form = getFacetForm();
  if (!form) return;

  const url = new URL(window.location.href);
  url.searchParams.set('section_id', getFacetSectionId(form));
  document.getElementById('FacetDrawer')?.hide?.();
  getFacetController(form).renderSection(url.toString(), null, { history: 'none', closeDrawer: false });
});

document.addEventListener('toggle', (event) => {
  const drawer = event.target;
  if (!(drawer instanceof HTMLElement) || drawer.id !== 'FacetDrawer') return;
  if (drawer.hasAttribute('open')) return;

  const form = drawer.querySelector('form.facet-form');
  if (form) getFacetController(form).resetDraft();
  });
}

class FacetCount extends HTMLElement {
  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    this.facetUpdateUnsubscriber?.();
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''), 10) || 0;
  }

  onFacetUpdate(event) {
    const parsedHTML = event.parsedHTML || new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    if (!facetCount) return;
    this.innerHTML = facetCount.innerHTML;
    this.hidden = this.itemCount === 0;
  }
}
if (!customElements.get('facet-count')) customElements.define('facet-count', FacetCount);

class FacetToggler extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.facetEl = document.getElementById('FacetFiltersContainer');
    this.addEventListener('click', this.onClickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick(event) {
    event.preventDefault();
    if (!this.facetEl) return;
    this.facetEl.classList.toggle('xl:block');
  }
}
if (!customElements.get('facet-toggler')) customElements.define('facet-toggler', FacetToggler, { extends: 'button' });

class LoadMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
    this.abortController = null;
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') === 'infinite') {
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
    this.abortController?.abort();
  }

  onClick() {
    if (this.classList.contains('btn--loading')) return;

    const url = this.setUrl().toString();
    this.loadingState();
    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Load more failed: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        this.renderProductGridContainer(parsedHTML);
        this.renderPagination(parsedHTML);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('[Banyomda] Load more failed.', error);
        this.classList.remove('pointer-events-none', 'btn--loading');
      });
  }

  renderPagination(parsedHTML) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (!productGridContainer) return;

    const pagination = productGridContainer.querySelector('.pagination');
    if (!pagination) return;

    const source = parsedHTML.querySelector('.pagination');

    if (source) {
      pagination.innerHTML = source.innerHTML;
    } else {
      pagination.remove();
    }
  }

  renderProductGridContainer(parsedHTML) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (!productGridContainer) return;

    const productList = productGridContainer.querySelector('.products-list');
    const source = parsedHTML.querySelector('.products-list');

    if (source && productList) {
      source.querySelectorAll('.f-column').forEach((item) => {
        productList.appendChild(item);
      });
    }
  }

  setUrl() {
    const action = this.getAttribute('action');
    if (!action) return new URL(window.location.href);

    const url = new URL(action, window.location.origin);
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none', 'btn--loading');
  }
}

if (!customElements.get('load-more-button')) customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'hypertheme:collection-layout';

    this.initLayoutMode();
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  /**
   * @param {event|element} evt
   */
  onButtonClick(evt) {
    const target = evt.currentTarget ? evt.currentTarget : evt;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (FoxTheme.config.hasLocalStorage) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);
        if (target) {
          this.changeLayout(target, layoutMode);
        }
      }
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;

    productGrid.dataset.layout = layoutMode;

    const { gridClass, listClass } = productGrid.dataset;
    if ((gridClass || listClass) && gridClass !== listClass) {
      const productCards = productGrid.querySelectorAll('.product-card');
      productCards &&
        productCards.forEach((card) => {
          if ('grid' === layoutMode) {
            listClass && card.classList.remove(listClass);
            gridClass && card.classList.add(gridClass);
          } else {
            gridClass && card.classList.remove(gridClass);
            listClass && card.classList.add(listClass);
          }
        });
    }

    this.buttons.forEach((button) => {
      button.classList.remove('btn--active');
    });
    target.classList.add('btn--active');

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}
if (!customElements.get('layout-switcher')) customElements.define('layout-switcher', LayoutSwitcher);
